/**
 * Customer Details Modal Component
 * 
 * This component displays detailed information about a specific customer
 * including their order history and statistics.
 */

import React, { useEffect, useState } from 'react';
import { useTranslations } from '../../context/TranslationsContext';
import { customersAPI } from '../../api';
import { useApp } from '../../context/AppContext';

const CustomerDetailsModal = ({ customer, onClose }) => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!customer || !state.token) return;
      
      setLoading(true);
      try {
        const details = await customersAPI.getCustomerByPhone(state.token, customer.phone_number);
        setCustomerDetails(details);
      } catch (error) {
        console.error('Error fetching customer details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [customer, state.token]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US');
  };

  const createGoogleMapsLink = (address) => {
    if (!address) return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(price);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!customer) return null;

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading">{t.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h3>{t.customerDetails}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <div className="customer-details-grid">
            <div className="detail-section">
              <h4>{t.customerInfo}</h4>
              <div className="detail-item">
                <span className="label">{t.customerName}:</span>
                <span className="value">{customer.name}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.phone}:</span>
                <span className="value">{customer.phone_number}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.address}:</span>
                <span className="value">
                  {customer.address ? (
                    <a 
                      href={createGoogleMapsLink(customer.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="address-link"
                      style={{
                        color: '#1976d2',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      {customer.address} 📍
                    </a>
                  ) : (
                    t.notSpecified
                  )}
                </span>
              </div>
              <div className="detail-item">
                <span className="label">{t.idNumber}:</span>
                <span className="value">{customer.id_number || t.notSpecified}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.createdAt}:</span>
                <span className="value">{formatDate(customer.created_at)}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>{t.customerStatistics}</h4>
              <div className="detail-item">
                <span className="label">{t.totalOrders}:</span>
                <span className="value">{customerDetails?.total_orders || 0}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.totalAmounts}:</span>
                <span className="value">{formatPrice(customerDetails?.total_spent || 0)} {t.sar}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t.status}:</span>
                <span className="value">{customer.status || t.active}</span>
              </div>
            </div>
          </div>

          {customerDetails?.orders && customerDetails.orders.length > 0 && (
            <div className="orders-section">
              <h4>{t.customerOrders}</h4>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t.orderNumber}</th>
                      <th>{t.items}</th>
                      <th>{t.space}</th>
                      <th>{t.duration}</th>
                      <th>{t.price}</th>
                      <th>{t.status}</th>
                      <th>{t.createdAt}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerDetails.orders.map(order => (
                      <tr key={order.id}>
                        <td>#{order.id.substring(0, 8)}</td>
                        <td>{order.items_description}</td>
                        <td>{order.space_required} {t.sqm}</td>
                        <td>{order.duration_months} {t.months}</td>
                        <td>{formatPrice(order.total_price)} {t.sar}</td>
                        <td>
                          <span className={`status-badge ${getStatusColor(order.status)}`}>
                            {t[order.status]}
                          </span>
                        </td>
                        <td>{formatDate(order.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;
